// NR decal cards: pointer tilt + reflective glare, size-chip price, quick add.
// Quick add goes through Shopify's standard cart actions, which Horizon wires to its
// cart drawer. Without them the form still posts to /cart/add as normal HTML.

const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const MAX_TILT = 7;

function bindTilt(stage) {
  if (stage.dataset.nrTiltReady) return;
  stage.dataset.nrTiltReady = '1';
  let frame = 0;

  stage.addEventListener('pointermove', (event) => {
    if (!finePointer.matches || reduceMotion.matches) return;
    const { clientX, clientY } = event;
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const r = stage.getBoundingClientRect();
      const x = (clientX - r.left) / r.width;
      const y = (clientY - r.top) / r.height;
      stage.style.setProperty('--ry', `${(x - 0.5) * MAX_TILT * 2}deg`);
      stage.style.setProperty('--rx', `${(0.5 - y) * MAX_TILT * 2}deg`);
      stage.style.setProperty('--gx', `${x * 100}%`);
      stage.style.setProperty('--gy', `${y * 100}%`);
    });
  });

  stage.addEventListener('pointerleave', () => {
    cancelAnimationFrame(frame);
    frame = 0;
    stage.style.setProperty('--rx', '0deg');
    stage.style.setProperty('--ry', '0deg');
  });
}

// Sticker slap: the decal art peels off the card, spins through the air and slaps onto
// the cart icon before the drawer opens. Pure WAAPI transforms; skipped for reduced motion.
async function slapToCart(form) {
  const art = form.closest('[data-nr-card]')?.querySelector('.nr-card__img') ?? form.closest('section')?.querySelector('.nr-pack__sticker img');
  const cart = document.querySelector('.header-actions__cart-icon');
  if (!art || !cart || reduceMotion.matches) return;

  const from = art.getBoundingClientRect();
  const to = cart.getBoundingClientRect();
  if (!from.width || !to.width) return;

  const ghost = art.cloneNode(true);
  ghost.removeAttribute('srcset');
  ghost.removeAttribute('sizes');
  ghost.src = art.currentSrc || art.src;
  ghost.alt = '';
  Object.assign(ghost.style, {
    position: 'fixed',
    left: `${from.left}px`,
    top: `${from.top}px`,
    width: `${from.width}px`,
    height: `${from.height}px`,
    margin: '0',
    padding: '0',
    border: '0',
    background: 'none',
    overflow: 'visible',
    objectFit: 'contain',
    zIndex: '2147483001',
    pointerEvents: 'none',
    filter: 'drop-shadow(0 12px 18px rgb(0 0 0 / .6))',
  });
  document.body.append(ghost);
  // Top layer, so the sticker flies above the cart drawer if Horizon has already opened it
  if ('showPopover' in ghost) {
    ghost.popover = 'manual';
    ghost.showPopover();
  }

  const dx = to.left + to.width / 2 - (from.left + from.width / 2);
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);
  const end = Math.max(to.width / from.width, 0.12);

  await ghost
    .animate(
      [
        { transform: 'translate(0, 0) rotate(0deg) scale(1)', offset: 0 },
        { transform: `translate(${dx * 0.35}px, ${dy * 0.35 - 80}px) rotate(-18deg) scale(.7)`, offset: 0.35 },
        { transform: `translate(${dx}px, ${dy}px) rotate(12deg) scale(${end * 1.6})`, offset: 0.85 },
        { transform: `translate(${dx}px, ${dy}px) rotate(-4deg) scale(${end})`, opacity: 0, offset: 1 },
      ],
      { duration: 720, easing: 'cubic-bezier(.5,0,.2,1)' }
    )
    .finished.catch(() => {});
  ghost.remove();
  cart.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.35) rotate(-8deg)' }, { transform: 'scale(1)' }], {
    duration: 280,
    easing: 'ease-out',
  });
}

// Quick add, delegated on document: works for cards and for forms inside the cart drawer,
// which Horizon re-renders after every cart change.
document.addEventListener('change', (event) => {
  const input = event.target;
  const form = input instanceof HTMLInputElement ? input.closest('form[data-nr-quick-add]') : null;
  const priceEl = form?.querySelector('[data-nr-add-price]');
  if (input.name === 'id' && input.dataset.price && priceEl) priceEl.textContent = input.dataset.price;
});

document.addEventListener('submit', async (event) => {
  const form = event.target instanceof HTMLFormElement && event.target.matches('[data-nr-quick-add]') ? event.target : null;
  if (!form) return;
  const actions = window.Shopify?.actions;
  if (!actions?.updateCart) return; // plain HTML post fallback
  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  const labelEl = form.querySelector('[data-nr-add-label]');
  const idleLabel = labelEl?.dataset.idle ?? labelEl?.textContent;
  if (labelEl) labelEl.dataset.idle = idleLabel;
  const id = new FormData(form).get('id');
  if (!id || button?.getAttribute('aria-busy') === 'true') return;

  const drawer = form.closest('dialog');
  button?.setAttribute('aria-busy', 'true');
  try {
    const result = await actions.updateCart({ lines: [{ merchandiseId: String(id), quantity: 1 }] });
    if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
    button?.classList.add('is-added');
    if (labelEl) labelEl.textContent = 'Added';
    await slapToCart(form);
    // Horizon may auto-open the drawer on cart change; don't slide it in twice, and never on /cart
    const inCart = form.closest('cart-items-component, dialog');
    if (!inCart && !document.querySelector('theme-drawer#cart-drawer[open], #cart-drawer dialog[open]')) actions.openCart?.();
    // The drawer re-renders after the add; keep keyboard focus inside it
    if (drawer) {
      setTimeout(() => {
        if (button?.isConnected) return;
        drawer.querySelector('.nr-gap__add, [name="checkout"], .cart__checkout-button, button')?.focus();
      }, 700);
    }
    setTimeout(() => {
      if (!button?.isConnected) return;
      button.classList.remove('is-added');
      if (labelEl) labelEl.textContent = idleLabel;
    }, 2200);
  } catch (error) {
    console.error(error);
    form.submit(); // let Shopify's cart page report the problem
  } finally {
    button?.removeAttribute('aria-busy');
  }
});

function init(root = document) {
  root.querySelectorAll('[data-nr-tilt]').forEach(bindTilt);
}

init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
